import { Check, Clock, Copy, Tag } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { copiarAlPortapapeles } from '@/lib/clipboard';

import { ETIQUETA_NIVEL, TONO_NIVEL } from '../lib/configuracion-format';
import { campoTexto, formatearRegistro, parsearRegistro } from '../lib/log-registro';
import type { LineaLog } from '../configuracion.types';

interface LogDetalleDialogoProps {
  linea: LineaLog;
  /** Archivo del que sale la línea (sólo informativo). */
  archivo: string | null;
  onClose: () => void;
}

/** Ficha de un dato del registro (etiqueta arriba, valor monoespaciado abajo). */
function Dato({ etiqueta, valor, icono }: { etiqueta: string; valor: string; icono?: ReactNode }) {
  return (
    <div className="rounded-control border border-ink-100 bg-ink-50/60 px-3 py-2">
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-400">
        {icono}
        {etiqueta}
      </p>
      <p className="mt-0.5 font-mono text-xs break-words text-ink-800">{valor}</p>
    </div>
  );
}

/**
 * Detalle de una línea de log, con el registro JSON formateado y ordenado.
 *
 * Se abre al pulsar una línea de la consola. Los logs de la app son una línea por
 * registro en JSON (pino) y leerlos en crudo obliga a copiarlos a un formateador;
 * aquí se muestran ya indentados y con los campos identificativos primero
 * (`time`, `level`, `logger_name`, `message`) y el resto alfabético, y se pueden
 * copiar en ese mismo formato. Las líneas que no son JSON (los logs del panel o
 * una continuación de stack trace) se muestran tal cual.
 */
export function LogDetalleDialogo({ linea, archivo, onClose }: LogDetalleDialogoProps) {
  const [copiado, setCopiado] = useState(false);

  const registro = parsearRegistro(linea.texto);
  const formateado = registro ? formatearRegistro(registro) : null;

  const message = campoTexto(registro, ['message', 'msg']);
  const logger = campoTexto(registro, ['logger_name', 'logger']);
  const appCode = campoTexto(registro, ['app_code']);
  const servicio = campoTexto(registro, ['service_type']);
  const version = campoTexto(registro, ['service_version']);
  const entorno = campoTexto(registro, ['environment']);

  const copiar = async () => {
    const listo = await copiarAlPortapapeles(formateado ?? linea.texto);

    setCopiado(listo);
    setTimeout(() => setCopiado(false), 1500);
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Línea ${linea.numero}`}
      description={[archivo, linea.hora ?? 'sin hora'].filter(Boolean).join(' · ')}
      size="xl"
      footer={
        <>
          <Button
            variant="outline"
            size="sm"
            className="mr-auto"
            onClick={() => void copiar()}
            aria-label={formateado ? 'Copiar el registro formateado' : 'Copiar la línea'}
          >
            {copiado ? (
              <Check className="size-3.5 text-emerald-600" aria-hidden="true" />
            ) : (
              <Copy className="size-3.5" aria-hidden="true" />
            )}
            {copiado ? 'Copiado' : formateado ? 'Copiar JSON' : 'Copiar línea'}
          </Button>

          <Button variant="secondary" size="sm" onClick={onClose}>
            Cerrar
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={TONO_NIVEL[linea.nivel]}>{ETIQUETA_NIVEL[linea.nivel]}</Badge>
          {logger ? <Badge variant="outline">{logger}</Badge> : null}
          <span className="font-mono text-[11px] text-ink-500">
            {[entorno, appCode, servicio, version].filter(Boolean).join(' · ')}
          </span>
        </div>

        {message ? (
          <div className="rounded-control border border-brand-100 bg-brand-50/60 px-3 py-2.5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-brand-700">
              Mensaje
            </p>
            <p className="mt-0.5 text-sm leading-relaxed break-words whitespace-pre-wrap text-ink-800">
              {message}
            </p>
          </div>
        ) : null}

        {registro ? (
          <>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              <Dato etiqueta="Hora" valor={linea.hora ?? '—'} icono={<Clock className="size-3.5" aria-hidden="true" />} />
              <Dato etiqueta="Nivel" valor={ETIQUETA_NIVEL[linea.nivel]} />
              <Dato
                etiqueta="Logger"
                valor={logger ?? '—'}
                icono={<Tag className="size-3.5" aria-hidden="true" />}
              />
            </div>

            <div>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-ink-400">
                Registro completo
              </p>
              <pre className="overflow-x-auto rounded-control border border-ink-100 bg-ink-950 p-3 font-mono text-[12px] leading-relaxed whitespace-pre-wrap break-words text-ink-100">
                {formateado}
              </pre>
              <p className="mt-1.5 text-[11px] text-ink-400">
                Campos identificativos primero (hora, nivel, logger, mensaje) y el resto en orden
                alfabético.
              </p>
            </div>
          </>
        ) : (
          <>
            <Alert variant="info">
              Esta línea no es un registro JSON (los logs del panel son texto plano y las
              continuaciones de un stack trace van en su propia línea), así que se muestra tal cual.
            </Alert>

            <pre className="overflow-x-auto rounded-control border border-ink-100 bg-ink-950 p-3 font-mono text-[12px] leading-relaxed whitespace-pre-wrap break-words text-ink-100">
              {linea.texto === '' ? '(línea vacía)' : linea.texto}
            </pre>
          </>
        )}
      </div>
    </Dialog>
  );
}
